<script setup lang="ts">
  import { ExclamationCircleIcon } from '@heroicons/vue/solid';
  import TopToRightAngleIcon from '~/assets/Icons/TopToRightAngleIcon.vue';
  import { formatToIndianCurrency, formatToIndianCurrencyBadge } from '~/functions/currencyUtilities';
  import type { BudgetExpensesStatus } from '~/types';

  const props = defineProps<{
    stats: BudgetExpensesStatus;
    inTableOf: 'Budget' | 'Grant';
  }>();

  const totalBudgetAmount = computed(() => {
    const value = +props.stats.total_budget_amount;
    return isNaN(value) ? 0 : value;
  });

  const approvedExpenses = computed(() => {
    const value = +props.stats.approved_expenses.within_budget + +props.stats.approved_expenses.exceeding_budget;
    return isNaN(value) ? 0 : value;
  });
  const approvedWithinBudgetPercentage = computed(() => {
    const percentage = (+props.stats.approved_expenses.within_budget / totalBudgetAmount.value) * 100;
    return isNaN(percentage) ? 0 : +percentage.toFixed(1);
  });
  const approvedExceedingBudgetPercentage = computed(() => {
    const percentage = (+props.stats.approved_expenses.exceeding_budget / totalBudgetAmount.value) * 100;
    return isNaN(percentage) ? 0 : +percentage.toFixed(1);
  });

  const pendingExpenses = computed(() => {
    const value = +(props.stats.pending_expenses?.pending_amount ?? 0);
    return isNaN(value) ? 0 : value;
  });
  const pendingWithinBudgetPercentage = computed(() => {
    const percentage = (+(props.stats.pending_expenses?.pending_within_budget ?? 0) / totalBudgetAmount.value) * 100;
    return isNaN(percentage) ? 0 : +percentage.toFixed(1);
  });
  const pendingExceedingBudgetPercentage = computed(() => {
    const percentage = (+(props.stats.pending_expenses?.pending_exceeding_budget ?? 0) / totalBudgetAmount.value) * 100;
    return isNaN(percentage) ? 0 : +percentage.toFixed(1);
  });
  const pendingExceedingExists = computed(() => (props.stats.pending_expenses?.pending_exceeding_budget ?? 0) > 0);

  const draftExpenses = computed(() => {
    const value = +(props.stats.draft_expenses?.draft_amount ?? 0);
    return isNaN(value) ? 0 : value;
  });
  const draftWithinBudgetPercentage = computed(() => {
    const percentage = (+(props.stats.draft_expenses?.draft_within_budget ?? 0) / totalBudgetAmount.value) * 100;
    return isNaN(percentage) ? 0 : +percentage.toFixed(1);
  });
  const draftExceedingBudgetPercentage = computed(() => {
    const percentage = (+(props.stats.draft_expenses?.draft_exceeding_budget ?? 0) / totalBudgetAmount.value) * 100;
    return isNaN(percentage) ? 0 : +percentage.toFixed(1);
  });
  const draftExceedingExists = computed(() => (props.stats.draft_expenses?.draft_exceeding_budget ?? 0) > 0);

  // const unapprovedExpenses = computed(() => {
  //   const value = +props.stats.unapproved_expenses.exceeding_budget + +props.stats.unapproved_expenses.within_budget;
  //   return isNaN(value) ? 0 : value;
  // });

  const currentBalance = computed(() => totalBudgetAmount.value - approvedExpenses.value);
  const potentialBalance = computed(
    () => totalBudgetAmount.value - approvedExpenses.value - pendingExpenses.value - draftExpenses.value
  );

  const percentageUtilized = computed(() => {
    const value = approvedWithinBudgetPercentage.value + approvedExceedingBudgetPercentage.value;
    if (isNaN(value)) return 0;
    if (!isFinite(value)) return 0;
    return value;
  });

  const calculateScaledPercentage = (percentage: number, total: number) => {
    if (total <= 100) return percentage;
    return (percentage / total) * 100;
  };

  const totalPercentage = computed(() => {
    return (
      approvedWithinBudgetPercentage.value +
      approvedExceedingBudgetPercentage.value +
      pendingWithinBudgetPercentage.value +
      pendingExceedingBudgetPercentage.value +
      draftWithinBudgetPercentage.value +
      draftExceedingBudgetPercentage.value
    );
  });

  const scaledApprovedWithinBudget = computed(() =>
    calculateScaledPercentage(approvedWithinBudgetPercentage.value, totalPercentage.value)
  );
  const scaledApprovedExceedingBudget = computed(() =>
    calculateScaledPercentage(approvedExceedingBudgetPercentage.value, totalPercentage.value)
  );
  const scaledPendingWithinBudget = computed(() =>
    calculateScaledPercentage(pendingWithinBudgetPercentage.value, totalPercentage.value)
  );
  const scaledPendingExceedingBudget = computed(() =>
    calculateScaledPercentage(pendingExceedingBudgetPercentage.value, totalPercentage.value)
  );
  const scaledDraftWithinBudget = computed(() =>
    calculateScaledPercentage(draftWithinBudgetPercentage.value, totalPercentage.value)
  );
  const scaledDraftExceedingBudget = computed(() =>
    calculateScaledPercentage(draftExceedingBudgetPercentage.value, totalPercentage.value)
  );
</script>

<template>
  <a-popover placement="bottom" :arrow="false">
    <template #content>
      <div class="w-[425px] flex flex-col py-2 px-3 gap-2 text-zinc-800">
        <!-- (A) Total Budget Amount -->
        <div class="bg-[#F4F4F5] border border-[#E4E4E7] rounded px-2 py-1.5 flex justify-between items-center">
          <p class="text-sm font-bold">Total {{ inTableOf }} Amount <span class="font-normal">(A)</span></p>
          <p class="text-sm font-bold text-[#15803D]">{{ formatToIndianCurrency(totalBudgetAmount) }}</p>
        </div>

        <!-- (B) Approved Expenses -->
        <div class="flex flex-col border border-[#E4E4E7] border-l-4 border-l-[#16A34A] bg-[#FAFAFA] rounded">
          <div class="flex justify-between items-center py-1.5 px-2 bg-[#F0FDF4] border-b border-[#BBF7D0] rounded-t">
            <p class="text-sm font-bold text-[#15803D]">Approved Expenses <span class="font-normal">(B)</span></p>
            <p class="text-sm font-bold text-[#15803D]">{{ formatToIndianCurrency(approvedExpenses) }}</p>
          </div>
          <div
            class="flex justify-between items-center pl-3 p-1"
            :class="props.stats.approved_expenses.within_budget == 0 ? 'opacity-50' : 'opacity-100'"
          >
            <div class="flex items-center gap-2">
              <TopToRightAngleIcon />
              <div class="w-[14px] h-[14px] bg-[#16A34A] rounded-full"></div>
              <p class="sm-medium">Within {{ inTableOf }} Amount</p>
            </div>
            <p class="sm-medium">
              {{
                props.stats.approved_expenses.within_budget == 0
                  ? '-'
                  : formatToIndianCurrency(+props.stats.approved_expenses.within_budget)
              }}
            </p>
          </div>
          <div
            class="flex justify-between items-center pl-3 p-1"
            :class="props.stats.approved_expenses.exceeding_budget == 0 ? 'opacity-50' : 'opacity-100'"
          >
            <div class="flex items-center gap-2">
              <TopToRightAngleIcon />
              <div class="w-[14px] h-[14px] bg-[#B91C1C] rounded-full"></div>
              <p class="sm-medium">Exceeding {{ inTableOf }} Amount</p>
            </div>
            <p class="sm-medium text-[#B91C1C]">
              {{
                props.stats.approved_expenses.exceeding_budget == 0
                  ? '-'
                  : formatToIndianCurrency(+props.stats.approved_expenses.exceeding_budget)
              }}
            </p>
          </div>
        </div>

        <!-- (C) On Approval -->
        <div class="flex flex-col border border-[#E4E4E7] border-l-4 border-l-[#EA580C] bg-[#FAFAFA] rounded">
          <div class="flex justify-between items-center py-1.5 px-2 border-b border-[#FED7AA] rounded-t bg-[#FFF7ED]">
            <p class="text-sm font-bold text-[#C2410C]">Expenses On Approval <span class="font-normal">(C)</span></p>
            <p class="text-sm font-bold text-[#C2410C]">{{ formatToIndianCurrency(pendingExpenses) }}</p>
          </div>
          <div
            class="flex justify-between items-center pl-3 p-1"
            :class="(props.stats.pending_expenses?.pending_within_budget ?? 0) == 0 ? 'opacity-50' : 'opacity-100'"
          >
            <div class="flex items-center gap-2">
              <TopToRightAngleIcon />
              <div class="w-[14px] h-[14px] relative rounded-full overflow-hidden">
                <div class="approval-dot-within-grant"></div>
              </div>
              <p class="sm-medium">Within {{ inTableOf }} Amount</p>
            </div>
            <p class="sm-medium">
              {{
                (props.stats.pending_expenses?.pending_within_budget ?? 0) == 0
                  ? '-'
                  : formatToIndianCurrency(+(props.stats.pending_expenses?.pending_within_budget ?? 0))
              }}
            </p>
          </div>
          <div
            class="flex justify-between items-center pl-3 p-1"
            :class="(props.stats.pending_expenses?.pending_exceeding_budget ?? 0) == 0 ? 'opacity-50' : 'opacity-100'"
          >
            <div class="flex items-center gap-2">
              <TopToRightAngleIcon />
              <div class="w-[14px] h-[14px] relative rounded-full overflow-hidden">
                <div class="approval-dot-exceeding-grant"></div>
              </div>
              <p class="sm-medium">Exceeding {{ inTableOf }} Amount</p>
            </div>
            <p class="sm-medium text-[#B91C1C]">
              {{
                (props.stats.pending_expenses?.pending_exceeding_budget ?? 0) == 0
                  ? '-'
                  : formatToIndianCurrency(+(props.stats.pending_expenses?.pending_exceeding_budget ?? 0))
              }}
            </p>
          </div>
        </div>

        <!-- (D) Expense Draft -->
        <div class="flex flex-col border border-[#E4E4E7] border-l-4 border-l-[#D97706] bg-[#FAFAFA] rounded">
          <div class="flex justify-between items-center py-1.5 px-2 border-b border-[#FDE68A] rounded-t bg-[#FFFBEB]">
            <p class="text-sm font-bold text-[#92400E]">Expense Draft <span class="font-normal">(D)</span></p>
            <p class="text-sm font-bold text-[#92400E]">{{ formatToIndianCurrency(draftExpenses) }}</p>
          </div>
          <div
            class="flex justify-between items-center pl-3 p-1"
            :class="(props.stats.draft_expenses?.draft_within_budget ?? 0) == 0 ? 'opacity-50' : 'opacity-100'"
          >
            <div class="flex items-center gap-2">
              <TopToRightAngleIcon />
              <div class="w-[14px] h-[14px] relative rounded-full overflow-hidden">
                <div class="draft-dot-within-budget"></div>
              </div>
              <p class="sm-medium">Within {{ inTableOf }} Amount</p>
            </div>
            <p class="sm-medium">
              {{
                (props.stats.draft_expenses?.draft_within_budget ?? 0) == 0
                  ? '-'
                  : formatToIndianCurrency(+(props.stats.draft_expenses?.draft_within_budget ?? 0))
              }}
            </p>
          </div>
          <div
            class="flex justify-between items-center pl-3 p-1"
            :class="(props.stats.draft_expenses?.draft_exceeding_budget ?? 0) == 0 ? 'opacity-50' : 'opacity-100'"
          >
            <div class="flex items-center gap-2">
              <TopToRightAngleIcon />
              <div class="w-[14px] h-[14px] relative rounded-full overflow-hidden">
                <div class="draft-dot-exceeding-budget"></div>
              </div>
              <p class="sm-medium">Exceeding {{ inTableOf }} Amount</p>
            </div>
            <p class="sm-medium text-[#B91C1C]">
              {{
                (props.stats.draft_expenses?.draft_exceeding_budget ?? 0) == 0
                  ? '-'
                  : formatToIndianCurrency(+(props.stats.draft_expenses?.draft_exceeding_budget ?? 0))
              }}
            </p>
          </div>
        </div>

        <!-- Current Balance (A - B) -->
        <div
          class="flex justify-between items-center p-1.5 rounded border"
          :class="currentBalance < 0 ? 'bg-[#FEF2F2] border-[#FECACA]' : 'bg-[#F0FDF4] border-[#BBF7D0]'"
        >
          <p class="text-sm font-bold">Current Balance <span class="font-normal">(A - B)</span></p>
          <p class="text-sm font-bold" :class="currentBalance < 0 ? 'text-[#B91C1C]' : 'text-[#15803D]'">
            <span v-if="currentBalance < 0">(-) </span>
            {{ formatToIndianCurrency(Math.abs(currentBalance)) }}
          </p>
        </div>

        <!-- Potential Balance (A - B - C - D) -->
        <div
          class="flex justify-between items-start p-1.5 rounded border"
          :class="potentialBalance < 0 ? 'bg-[#FEF2F2] border-[#FECACA]' : 'bg-[#F0FDF4] border-[#BBF7D0]'"
        >
          <div class="flex gap-2">
            <div class="w-[14px] h-[14px] bg-[#D4D4D8] rounded-full mt-[3px] shrink-0"></div>
            <div class="flex flex-col">
              <p class="text-sm font-bold">Potential Balance <span class="font-normal">(A - B - C - D)</span></p>
              <p class="text-xs font-normal text-zinc-500">Balance if all expenses are approved</p>
            </div>
          </div>
          <p
            class="text-sm font-bold shrink-0 ml-2"
            :class="potentialBalance < 0 ? 'text-[#B91C1C]' : 'text-[#15803D]'"
          >
            <span v-if="potentialBalance < 0">(-) </span>
            {{ formatToIndianCurrency(Math.abs(potentialBalance)) }}
          </p>
        </div>
      </div>
    </template>

    <!-- Trigger cell content -->
    <div class="w-full h-full flex flex-col gap-1 items-center text-xs rounded px-1.5 py-1">
      <div class="flex justify-between w-full">
        <div
          class="text-[#166534] font-medium bg-[#F0FDF4] rounded px-1 py-0.5 flex gap-1"
          :class="percentageUtilized > 100 ? 'border border-[#B91C1C]' : ''"
        >
          <p>
            <span class="font-bold">{{ percentageUtilized }}%</span> Utilised
          </p>
          <ExclamationCircleIcon v-if="percentageUtilized > 100" class="w-4 h-4 text-[#B91C1C]" />
        </div>
        <span class="bg-[#FAFAFA] text-[#18181B] px-1 py-0.5 rounded">
          <span class="font-bold"
            ><span v-if="currentBalance < 0">(-) </span>{{ formatToIndianCurrency(Math.abs(currentBalance)) }}</span
          >
          Current Bal.
        </span>
      </div>

      <!-- Progress bar -->
      <div class="w-full h-2.5 bg-[#F4F4F5] rounded-full relative overflow-hidden flex border border-[#E4E4E7] p-0.5">
        <div class="approved-bar-within-budget" :style="{ width: scaledApprovedWithinBudget + '%', left: 0 }"></div>
        <div
          class="approval-bar-within-budget"
          :style="{ width: scaledPendingWithinBudget + '%', left: scaledApprovedWithinBudget + '%' }"
        ></div>
        <div
          class="draft-bar-within-budget"
          :style="{
            width: scaledDraftWithinBudget + '%',
            left: scaledApprovedWithinBudget + scaledPendingWithinBudget + '%'
          }"
        ></div>
        <div
          class="approved-bar-exceeding-budget"
          :style="{
            width: scaledApprovedExceedingBudget + '%',
            left: scaledApprovedWithinBudget + scaledPendingWithinBudget + scaledDraftWithinBudget + '%'
          }"
        ></div>
        <div
          class="approval-bar-exceeding-budget"
          :style="{
            width: scaledPendingExceedingBudget + '%',
            left:
              scaledApprovedWithinBudget +
              scaledPendingWithinBudget +
              scaledDraftWithinBudget +
              scaledApprovedExceedingBudget +
              '%'
          }"
        ></div>
        <div
          class="draft-bar-exceeding-budget"
          :style="{
            width: scaledDraftExceedingBudget + '%',
            left:
              scaledApprovedWithinBudget +
              scaledPendingWithinBudget +
              scaledDraftWithinBudget +
              scaledApprovedExceedingBudget +
              scaledPendingExceedingBudget +
              '%'
          }"
        ></div>
      </div>

      <!-- Inline badges -->
      <div class="flex w-full bg-[#fff9f3] rounded overflow-hidden">
        <!-- On Approval badge -->
        <div class="flex items-center gap-1 px-1.5 py-0.5 flex-1 min-w-0">
          <p class="text-[#9A3412] text-xs font-semibold shrink-0">On Approval</p>
          <div class="flex items-center gap-0.5">
            <span class="text-[#9A3412] text-xs font-bold whitespace-nowrap">{{
              formatToIndianCurrencyBadge(pendingExpenses)
            }}</span>
            <ExclamationCircleIcon v-if="pendingExceedingExists" class="w-3.5 h-3.5 text-[#B91C1C] shrink-0" />
          </div>
        </div>
        <!-- Draft badge (always shown) -->
        <div class="w-px bg-[#FED7AA] shrink-0 my-1"></div>
        <div class="flex-1 flex items-center gap-1 px-1.5 py-0.5 min-w-0">
          <p class="text-[#9A3412] text-xs font-semibold shrink-0">Draft</p>
          <div class="flex items-center gap-0.5">
            <span class="text-[#9A3412] text-xs font-bold whitespace-nowrap">{{
              formatToIndianCurrencyBadge(draftExpenses)
            }}</span>
            <ExclamationCircleIcon v-if="draftExceedingExists" class="w-3.5 h-3.5 text-[#B91C1C] shrink-0" />
          </div>
        </div>
      </div>
    </div>
  </a-popover>
</template>

<style>
  .ant-popover .ant-popover-inner {
    padding: 0 !important;
  }
</style>

<style scoped>
  .approved-bar-within-budget {
    height: 100%;
    background-color: #16a34a;
    border-radius: 0px;
    position: absolute;
    top: 0;
    animation: fillWidth 0.5s ease-out forwards;
  }

  .approval-bar-within-budget {
    height: 100%;
    background: repeating-linear-gradient(125deg, #15803d, #15803d 4px, transparent 4px, transparent 8px), #16a34a;
    border-radius: 0px;
    position: absolute;
    top: 0;
    animation: fillWidth 0.5s ease-out forwards;
  }

  .draft-bar-within-budget {
    height: 100%;
    background: repeating-linear-gradient(45deg, #15803d 0px, #15803d 1.5px, transparent 1.5px, transparent 6px),
      repeating-linear-gradient(135deg, #15803d 0px, #15803d 1.5px, transparent 1.5px, transparent 6px), #16a34a;
    border-radius: 0px;
    position: absolute;
    top: 0;
    animation: fillWidth 0.5s ease-out forwards;
  }

  .approved-bar-exceeding-budget {
    height: 100%;
    background-color: #b91c1c;
    border-radius: 0px;
    position: absolute;
    top: 0;
    animation: fillWidth 0.5s ease-out forwards;
  }

  .approval-bar-exceeding-budget {
    height: 100%;
    background: repeating-linear-gradient(125deg, #991b1b, #991b1b 4px, transparent 4px, transparent 8px), #b91c1c;
    border-radius: 0px;
    position: absolute;
    top: 0;
    animation: fillWidth 0.5s ease-out forwards;
  }

  .draft-bar-exceeding-budget {
    height: 100%;
    background: repeating-linear-gradient(45deg, #991b1b 0px, #991b1b 1.5px, transparent 1.5px, transparent 6px),
      repeating-linear-gradient(135deg, #991b1b 0px, #991b1b 1.5px, transparent 1.5px, transparent 6px), #b91c1c;
    border-radius: 0px;
    position: absolute;
    top: 0;
    animation: fillWidth 0.5s ease-out forwards;
  }

  .approval-dot-within-grant {
    width: 100%;
    height: 100%;
    background: repeating-linear-gradient(125deg, #15803d, #15803d 3px, transparent 3px, transparent 6px), #16a34a;
    border-radius: 10px;
    position: absolute;
    top: 0;
    left: 0;
  }

  .approval-dot-exceeding-grant {
    width: 100%;
    height: 100%;
    background: repeating-linear-gradient(125deg, #991b1b, #991b1b 3px, transparent 3px, transparent 6px), #b91c1c;
    border-radius: 10px;
    position: absolute;
    top: 0;
    left: 0;
  }

  .draft-dot-within-budget {
    width: 100%;
    height: 100%;
    background: repeating-linear-gradient(45deg, #15803d 0px, #15803d 1.5px, transparent 1.5px, transparent 5px),
      repeating-linear-gradient(135deg, #15803d 0px, #15803d 1.5px, transparent 1.5px, transparent 5px), #16a34a;
    border-radius: 10px;
    position: absolute;
    top: 0;
    left: 0;
  }

  .draft-dot-exceeding-budget {
    width: 100%;
    height: 100%;
    background: repeating-linear-gradient(45deg, #991b1b 0px, #991b1b 1.5px, transparent 1.5px, transparent 5px),
      repeating-linear-gradient(135deg, #991b1b 0px, #991b1b 1.5px, transparent 1.5px, transparent 5px), #b91c1c;
    border-radius: 10px;
    position: absolute;
    top: 0;
    left: 0;
  }

  @keyframes fillWidth {
    from {
      transform: scaleX(0);
      transform-origin: left;
    }
    to {
      transform: scaleX(1);
      transform-origin: left;
    }
  }
</style>
